import { WithId, WithTimestamps } from "../common";

export interface UserAddress {
  id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  address_line_one: string;
  address_line_two?: string;
  city: string;
  state: string;
  postal_code: string;
  billing_address?: boolean;
}

export interface StoreAddress {
  id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  address_line_one: string;
  address_line_two?: string;
  city: string;
  state: string;
  postal_code: string;
  billing_address?: boolean;
  store_type?: string;
}

export interface CustomerAddress {
  id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  address_line_one: string;
  address_line_two?: string;
  city: string;
  state: string;
  postal_code: string;
  billing_address?: boolean;
  user_id?: string;
  created_by?: string;
}

export interface Customer {
  id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  phone_number?: string;
  first_name?: string;
  email?: string;
  last_name?: string;
  total_orders?: number;
  total_spent?: number;
  temp_uuid?: string;
  default_address_id?: string;
  customer_addresses?: CustomerAddress[];
  carts?: any;
  SellerStores?: any;
  enabled?: boolean;
}

export interface CartItem {
  id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  variant_id: string;
  product_id: string;
  quantity: number;
  cart_id: string;
}

export interface ShippingDetails {
  weight: number;
  weight_unit: string;
  width: number;
  height: number;
  length: number;
  shipping_class: string;
  free_shipping: boolean;
  shipping_price: number;
}

export interface Metafield {
  key: string;
  value: string;
  type: string;
}

export interface Asset {
  id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  path: string;
  store_id: string;
  original_name: string;
  size: number;
  content_type: string;
  usage: string;
}

export interface VariantAsset {
  id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  variant_id: string;
  asset_id: string;
  position: number;
  usage: string;
  asset: Asset;
}

export interface Variant {
  id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  sku: string;
  product_id: string;
  product_slug: string;
  title: string;
  description: string | null;
  inventory: number;
  continue_selling_oos: boolean;
  taxable: boolean;
  price: number;
  listing_price: number;
  option1: string;
  option2: string;
  option3: string;
  supported_payment_types?: PaymentType[] | null;
  semi_prepaid_amount?: number;
  metafields: Metafield[] | null;
  owner_type: string;
  owner_id: string;
  track_inventory: boolean;
  shipping_details: ShippingDetails;
  assets?: VariantAsset[];
}

export interface OrderItem {
  id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  product_id: string;
  product_slug: string;
  title: string;
  description: string | null;
  inventory: number;
  continue_selling_oos: boolean;
  taxable: boolean;
  price: number;
  listing_price: number;
  option1: string;
  option2: string;
  option3: string;
  supported_payment_types?: PaymentType[] | null;
  semi_prepaid_amount?: number;
  metafields: Metafield[] | null;
  owner_type: string;
  owner_id: string;
  track_inventory: boolean;
  shipping_details: ShippingDetails;
  sku: string;
  quantity: number;
  purchased_at: string;
  order_set_id: string;
  assets?: VariantAsset[]; // Variant assets for image display
}

export interface PlatformCharge {
  name: string;
  amount: number;
}

export interface OrderNote {
  id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  order_set_id: string;
  title: string;
  description: string;
  created_by: string;
}

export interface StageHistory {
  id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  order_set_id: string;
  stage: string;
  previous_stage: string;
  changed_by: string;
  changed_by_id: string | null;
  reason: string;
  notes: string;
  timestamp: string;
}

export interface OrderShippingDetails {
  id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  order_set_id: string;
  shipping_provider: string;
  awb_code: string;
  awb_file_url: string;
  label_url: string;
  manifest_url: string;
  shipment_id: string;
  shipment_order_id: string;
  tracking_url: string;
  tracking_id: string;
  status_code: string;
  shipment_status: string;
  courier_company_id: string;
  courier_name: string;
  courier_option_id: string;
  parent_courier_name: string;
  routing_code: string;
  rto_routing_code: string;
  delivery_charges: number;
  rto_cost: number;
  chargeable_weight: number;
  applied_weight: number;
  dead_weight: number;
  package_length: number;
  package_breadth: number;
  package_height: number;
  shipment_type: string;
  freight_mode: string;
  expected_pickup: string;
  expected_delivery: string;
  current_courier_edd: string;
  awb_assigned_date: string | null;
  pickup_scheduled_date: string | null;
  pickup_completed_date: string | null;
  delivered_date: string | null;
  rto_delivered_date: string | null;
  latest_ndr_reason_code: string;
  latest_ndr_reason_desc: string;
  latest_ndr_date: string | null;
  collectable_amount: number;
  prepaid_amount: number;
  pickup_name: string;
  invoice_number: string;
  raw_provider_response: string;
}

export interface OrderSet {
  id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  order_id: string;
  store_id: string;
  shipping_type: ShippingType;
  store_type: string;
  needs_verification: boolean; // needs_wholesaler_verification in Go
  calculation?: Calculation;
  items: OrderItem[];
  charges: PlatformCharge[] | null;
  notes: OrderNote[];
  stage_history?: StageHistory[];
  shipping_details: OrderShippingDetails;
  pickup_address_id: string;
  pickup_address: StoreAddress;
  address?: UserAddress | StoreAddress; // Shipping/delivery address
  shipping_status?: string; // Current shipping status (alias for current_shipping_stage)
  current_shipping_stage: string;
  current_shipping_vendor_stage: string;
}

export interface Calculation {
  items: any;
  charges: any;
  discounts: any;
  total_taxes: any[];
  total_quantity: number;
  items_sub_total: number;
  charge_sub_total: number;
  total_charges: number;
  discount_sub_total: number;
  total_discounts: number;
  total_tax: number;
  grand_total: number;
  grand_total_int: number;
}

export type OrderStatus =
  | "CREATED"
  | "CONFIRMED"
  | "SHIPPING"
  | "RETURNING"
  | "COMPLETED"
  | "CANCELLED";

export type PaymentStatus =
  | "NULL"
  | "CREATED"
  | "AUTHORIZED"
  | "CAPTURED"
  | "FAILED"
  | "REFUNDED"
  | "CANCELLED";

export type PaymentType = "PREPAID" | "POSTPAID" | "SEMIPREPAID";

export interface OrderBase {
  user_id: string;
  customer?: Customer;
  order_number?: number;
  order_sets: OrderSet[];
  billing_address_different_than_shipping?: boolean;
  items: CartItem[];
  variants: Variant[];
  store_id: string;
  discount_codes?: string[];
  calculation: Calculation;
  shipping_address_id?: string;
  shipping_address?: UserAddress;
  billing_address_id?: string;
  billing_address?: UserAddress;
  supported_payment_type: PaymentType[];
  used_payment_type?: PaymentType;
  payment_id?: string;
  payment_status?: PaymentStatus;
  current_order_stage: OrderStatus;
  current_payment_stage: PaymentStatus;
  needs_seller_verification?: boolean;
}

export type Order = WithId<WithTimestamps<OrderBase>>;

// Action types
export const FETCH_ORDERS_REQUEST = "FETCH_ORDERS_REQUEST";
export const FETCH_ORDERS_SUCCESS = "FETCH_ORDERS_SUCCESS";
export const FETCH_ORDERS_FAILURE = "FETCH_ORDERS_FAILURE";

export const SEARCH_ORDERS_REQUEST = "SEARCH_ORDERS_REQUEST";
export const SEARCH_ORDERS_SUCCESS = "SEARCH_ORDERS_SUCCESS";
export const SEARCH_ORDERS_FAILURE = "SEARCH_ORDERS_FAILURE";

export const FETCH_ORDER_DETAILS_REQUEST = "FETCH_ORDER_DETAILS_REQUEST";
export const FETCH_ORDER_DETAILS_SUCCESS = "FETCH_ORDER_DETAILS_SUCCESS";
export const FETCH_ORDER_DETAILS_FAILURE = "FETCH_ORDER_DETAILS_FAILURE";

export const APPROVE_ORDER_REQUEST = "APPROVE_ORDER_REQUEST";
export const APPROVE_ORDER_SUCCESS = "APPROVE_ORDER_SUCCESS";
export const APPROVE_ORDER_FAILURE = "APPROVE_ORDER_FAILURE";

export const REJECT_ORDER_REQUEST = "REJECT_ORDER_REQUEST";
export const REJECT_ORDER_SUCCESS = "REJECT_ORDER_SUCCESS";
export const REJECT_ORDER_FAILURE = "REJECT_ORDER_FAILURE";

// Fetch options interface
export interface FetchOrdersOptions {
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  status?: OrderStatus;
  created_after?: string;
  created_before?: string;
}

export interface SearchOrdersOptions {
  q: string;
  page?: number;
  limit?: number;
}

export enum ShippingType {
  EXTERNAL = "EXTERNAL",
  INTERNAL = "INTERNAL",
}
